import { Descriptions } from 'antd';
import Modal from 'react-bootstrap/Modal';
import { useEffect, useRef, useState } from 'react';
import useBottomRemark from './useBottomRemark';
import { FaCircleInfo } from "react-icons/fa6"
import { Tooltip } from 'react-tooltip';
import EditConfirmationModal from '../../../../components/modal/EditConfirmationModal';
import ActionButtonGradient from '../../../../components/button/ActionButtonGradient';

const BottomRemark = ({
    show, 
    handleClose, 
    user,
    setCalibratedScore,
    calibratedScore,
    id,
    watch,
    phaseBefore,
    onJustificationSaved
  }) => {

  const {
    payload,
    setPayload,
    handleOnChange,
    getBottomRemark,
    postBottomRemarks,
    isSaving,
  } = useBottomRemark()

  // Track if data has been initialized to prevent saving empty initial state
  const isInitialized = useRef(false);
  // Track current user to detect user changes
  const currentUserRef = useRef(null);
  // Track if data was loaded from localStorage
  const loadedFromLocalStorage = useRef(false);
  // Track if we just loaded data to prevent immediate re-save
  const justLoaded = useRef(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [hasExistingJustification, setHasExistingJustification] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Helper function to count letters (excluding spaces)
  const countLetters = (text) => {
    if (!text) return 0;
    return text.replace(/\s/g, '').length;
  };

  // Reset state when user changes
  useEffect(() => {
    const currentUserKey = `${user.ID || user.Nik || user.Name}`;
    if (currentUserRef.current && currentUserRef.current !== currentUserKey) {
      isInitialized.current = false;
      loadedFromLocalStorage.current = false;
    }
    currentUserRef.current = currentUserKey;
  }, [user]);

  useEffect(() =>{
    // Only run when modal opens (show becomes true)
    if (!show) {
      isInitialized.current = false; // Reset when modal closes
      loadedFromLocalStorage.current = false;
      // Reset all state variables when modal closes
      setHasUnsavedChanges(false);
      setOriginalFormData(null);
      setHasExistingJustification(false);
      return;
    }
    
    // Reset all tracking states at the start of each modal open
    loadedFromLocalStorage.current = false;
    setHasUnsavedChanges(false);
    setOriginalFormData(null);
    setHasExistingJustification(false);
    
    // ALWAYS call API first to check for existing justification
    getBottomRemark(user.ProjectID, user.EmployeeID, user.ProjectPhaseID).then(() => {
      
      // Mark as initialized after API completes
      isInitialized.current = true;
      loadedFromLocalStorage.current = false;
      justLoaded.current = true;
    });
  }, 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [show])
  
  // Store original form data after payload is updated from API
  useEffect(() => {
    if (show && isInitialized.current && justLoaded.current && payload && Object.keys(payload).length > 0) {
      const hasData = ['LowPerformance', 'Indisipliner', 'Attitude', 'WarningLetter'].some(
        key => payload[key] && payload[key].trim() !== ''
      );
      
      setHasExistingJustification(hasData);
      setOriginalFormData(JSON.parse(JSON.stringify(payload)));
      justLoaded.current = false;
    }
  }, [show, payload]);

  // Check if form has been modified
  useEffect(() => {
    if (!show || !originalFormData || !payload || !isInitialized.current || justLoaded.current) {
      setHasUnsavedChanges(false);
      return;
    }
    
    const formHasChanged = JSON.stringify(payload) !== JSON.stringify(originalFormData);
    setHasUnsavedChanges(formHasChanged);
  }, [payload, originalFormData, show]);

  const handleSave = () => {
    postBottomRemarks(user.ProjectID, user.EmployeeID, user.ProjectPhaseID, handleClose);
    let newPayload = {...calibratedScore};
    newPayload[id].FilledTopBottomMark = true;
    setCalibratedScore(newPayload);
    
    setHasUnsavedChanges(false);
    setOriginalFormData(null);
    setHasExistingJustification(false);
  };

  const handleModalClose = () => {
    // Show confirmation if user made changes
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmSave = () => {
    setShowUnsavedChangesModal(false);
    handleSave();
  };

  const handleDiscardChanges = () => {
    // Restore original data
    if (originalFormData) {
      setPayload(JSON.parse(JSON.stringify(originalFormData)));
    }
    
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    handleClose();
  };

  // console.log(payload)

  return (
    <>
      <style>
        {`
          .modern-form-group {
            margin-bottom: 1.5rem;
          }
          .modern-label {
            display: block;
            font-weight: 500;
            font-size: 14px;
            color: #333;
            margin-bottom: 8px;
          }
          .modern-textarea {
            width: 100%;
            padding: 12px;
            border: 1px solid #d9d9d9;
            border-radius: 8px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
            transition: border-color 0.3s ease, box-shadow 0.3s ease;
          }
          .modern-textarea:focus {
            outline: none;
            border-color: #02B4CC;
            box-shadow: 0 0 0 2px rgba(2, 180, 204, 0.1);
          }
          .modern-textarea:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
          }
          .info-icon-inline {
            color: #02B4CC;
            cursor: help;
          }
          .char-counter {
            font-size: 12px;
            color: #666;
            margin-top: 4px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .char-counter.error {
            color: #dc3545;
          }
          .char-counter.success {
            color: #28a745;
          }
        `}
      </style>
      <Modal show={show} onHide={handleModalClose} dialogClassName='spmo-justification-modal' backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title><b>Justification Form (C, D, Decrease 2 Rating)</b></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Descriptions 
            bordered 
            size="small" 
            column={1}
            style={{ marginBottom: '20px' }}
            labelStyle={{ textAlign: 'left', width: '30%' }}
            contentStyle={{ textAlign: 'left', width: '70%' }}
          >
            <Descriptions.Item label="Employee Code">{user.Nik}</Descriptions.Item>
            <Descriptions.Item label="Employee Name">{user.Name}</Descriptions.Item>
            <Descriptions.Item label="Designation">{user.Designation}</Descriptions.Item>
          </Descriptions>
          
          <div align="left" className='mt-3'>
            <h5 style={{ 
              color: '#02B4CC', 
              borderBottom: '2px solid #02B4CC', 
              paddingBottom: '8px',
              marginBottom: '20px',
              fontWeight: '600'
            }}>
              Please fill in at least 1 justification in the categories below
            </h5>
            <div style={{ padding: '8px 0' }}>
              <form onSubmit={(e) => e.preventDefault()}> 
                {/* Low Performance */}
                <div className="modern-form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="modern-label" style={{ 
                    display: 'block',
                    fontWeight: '500',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Performance Issue
                    <FaCircleInfo
                      fontSize={14}
                      className="ms-2"
                      style={{color: "#02B4CC", cursor: "help"}}
                      data-tooltip-id="tooltip-lowperformance"
                    />
                  </label>
                  <textarea
                    required
                    rows={3}
                    name="LowPerformance"
                    className="modern-textarea"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    placeholder="Explain the performance issue of the employee related to core responsibilities (minimum 20 characters)"
                    onChange={handleOnChange}
                    onFocus={() => setFocusedField('LowPerformance')}
                    onBlur={() => setFocusedField(null)}
                    value={payload.LowPerformance || ''}
                  />
                  {focusedField === 'LowPerformance' && (
                    <div className={`char-counter ${countLetters(payload.LowPerformance) < 20 ? 'error' : 'success'}`}>
                      <span>{countLetters(payload.LowPerformance) < 20 ? `${20 - countLetters(payload.LowPerformance)} more characters needed` : 'Minimum requirement met ✓'}</span>
                      <span>{payload.LowPerformance?.length || 0}</span>
                    </div>
                  )}
                </div>

                {/* Indisipliner */}
                <div className="modern-form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="modern-label" style={{ 
                    display: 'block',
                    fontWeight: '500',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Behaviour Issue
                    <FaCircleInfo
                      fontSize={14}
                      className="ms-2"
                      style={{color: "#02B4CC", cursor: "help"}}
                      data-tooltip-id="tooltip-indisipliner"
                    />
                  </label>
                  <textarea
                    required
                    rows={3}
                    name="Indisipliner"
                    className="modern-textarea"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    placeholder="Explain the employee's lack of behaviour regarding company policies (minimum 20 characters)"
                    onChange={handleOnChange}
                    onFocus={() => setFocusedField('Indisipliner')}
                    onBlur={() => setFocusedField(null)}
                    value={payload.Indisipliner || ''}
                  />
                  {focusedField === 'Indisipliner' && (
                    <div className={`char-counter ${countLetters(payload.Indisipliner) < 20 ? 'error' : 'success'}`}>
                      <span>{countLetters(payload.Indisipliner) < 20 ? `${20 - countLetters(payload.Indisipliner)} more characters needed` : 'Minimum requirement met ✓'}</span>
                      <span>{payload.Indisipliner?.length || 0}</span>
                    </div>
                  )}
                </div>

                {/* Attitude */}
                <div className="modern-form-group" style={{ marginBottom: '1.5rem' }} hidden={true}>
                  <label className="modern-label" style={{ 
                    display: 'block',
                    fontWeight: '500',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Attitude
                    <FaCircleInfo
                      fontSize={14}
                      className="ms-2"
                      style={{color: "#02B4CC", cursor: "help"}}
                      data-tooltip-id="tooltip-attitude"
                    />
                  </label>
                  <textarea
                    required
                    rows={3}
                    name="Attitude"
                    className="modern-textarea"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    placeholder="Explain the negative attitudes of employees that are inconsistent with the norms within the scope of work (minimum 20 characters)"
                    onChange={handleOnChange}
                    onFocus={() => setFocusedField('Attitude')}
                    onBlur={() => setFocusedField(null)}
                    value={payload.Attitude || ''}
                  />
                  {focusedField === 'Attitude' && (
                    <div className={`char-counter ${countLetters(payload.Attitude) < 20 ? 'error' : 'success'}`}>
                      <span>{countLetters(payload.Attitude) < 20 ? `${20 - countLetters(payload.Attitude)} more characters needed` : 'Minimum requirement met ✓'}</span>
                      <span>{payload.Attitude?.length || 0}</span>
                    </div>
                  )}
                </div>

                {/* Warning Letter */}
                <div className="modern-form-group" style={{ marginBottom: '1.5rem' }} hidden={true}>
                  <label className="modern-label" style={{ 
                    display: 'block',
                    fontWeight: '500',
                    fontSize: '14px',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Warning Letter
                    <FaCircleInfo
                      fontSize={14}
                      className="ms-2"
                      style={{color: "#02B4CC", cursor: "help"}}
                      data-tooltip-id="tooltip-warningletter"
                    />
                  </label>
                  <textarea
                    required
                    rows={3}
                    name="WarningLetter"
                    className="modern-textarea"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    placeholder="Data related to the warning letter received by the employee from the company (minimum 20 characters)"
                    onChange={handleOnChange}
                    onFocus={() => setFocusedField('WarningLetter')}
                    onBlur={() => setFocusedField(null)}
                    value={payload.WarningLetter || ''}
                  />
                  {focusedField === 'WarningLetter' && (
                    <div className={`char-counter ${countLetters(payload.WarningLetter) < 20 ? 'error' : 'success'}`}>
                      <span>{countLetters(payload.WarningLetter) < 20 ? `${20 - countLetters(payload.WarningLetter)} more characters needed` : 'Minimum requirement met ✓'}</span>
                      <span>{payload.WarningLetter?.length || 0}</span>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
          <Tooltip
                id="tooltip-lowperformance"
                style={{ 
                    backgroundColor: "#efefef", 
                    color: "#222", 
                    width: "20rem",
                    textAlign: "left"
                }}
            >
                <span>Explain the performance issue of the employee related to core responsibilities. Minimum length: 20 characters</span>
            </Tooltip>
            <Tooltip
                id="tooltip-indisipliner"
                style={{ 
                    backgroundColor: "#efefef", 
                    color: "#222", 
                    width: "20rem",
                    textAlign: "left"
                }}
            >
                <span>Explain the employee's lack of behaviour regarding company policies. Minimum length: 20 characters</span>
            </Tooltip>
            <Tooltip
                id="tooltip-attitude"
                style={{ 
                    backgroundColor: "#efefef", 
                    color: "#222", 
                    width: "20rem",
                    textAlign: "left"
                }}
            >
                <span>Explain the negative attitudes of employees that are inconsistent with the norms within the scope of work. Minimum length: 20 characters</span>
            </Tooltip>
            <Tooltip
                id="tooltip-warningletter"
                style={{ 
                    backgroundColor: "#efefef", 
                    color: "#222", 
                    width: "20rem",
                    textAlign: "left"
                }}
            >
                <span>Data related to the warning letter received by the employee from the company. Minimum length: 20 characters</span>
            </Tooltip>
        </Modal.Body>
        <Modal.Footer>
        {
          watch ?
          <>
            <ActionButtonGradient
              onClick={handleModalClose}
              variant='danger'
              children={"Close"}
            />
          </>:
          <>
            <ActionButtonGradient
              onClick={handleSave}
              variant='primary'
              disabled={isSaving}
              children={isSaving ? "Saving..." : "Save"}
            />
          </>
        }
        </Modal.Footer>
      </Modal>
      
      {/* Unsaved Changes Confirmation Modal */}
      <EditConfirmationModal
        show={showUnsavedChangesModal}
        onHide={() => setShowUnsavedChangesModal(false)}
        onConfirm={handleConfirmSave}
        onCancel={handleDiscardChanges}
        title="Save Changes"
        message="You have unsaved changes. Do you want to save them?"
        confirmText="Submit"
        cancelText="Cancel"
      />
    </>
  );
}

export default BottomRemark